import { Link } from "react-router-dom";
import { 
  Clock, 
  Calendar,
  Users,
  MessageSquare,
  ListTodo,
  Glasses,
  Waves,
  BookOpen,
  GraduationCap
} from "lucide-react";

interface Platform {
  name: string;
  tagline: string;
  shutdownYear: string;
  alternativeUrl: string;
  icon: React.ReactNode;
  color: string;
}

const allPlatforms: Platform[] = [
  { name: "Groove", tagline: "Virtual Coworking", shutdownYear: "2025", alternativeUrl: "/groove/", icon: <Users className="w-4 h-4" />, color: "bg-blue-500/10 text-blue-500" },
  { name: "Studyverse", tagline: "Virtual Study Rooms", shutdownYear: "2024", alternativeUrl: "/studyverse/", icon: <BookOpen className="w-4 h-4" />, color: "bg-purple-500/10 text-purple-500" },
  { name: "Edmodo", tagline: "Classroom Network", shutdownYear: "2022", alternativeUrl: "/edmodo/", icon: <GraduationCap className="w-4 h-4" />, color: "bg-orange-500/10 text-orange-500" },
  { name: "OpenStudy", tagline: "Peer Learning", shutdownYear: "2015", alternativeUrl: "/openstudy/", icon: <MessageSquare className="w-4 h-4" />, color: "bg-green-500/10 text-green-500" },
  { name: "Wunderlist", tagline: "To-Do App", shutdownYear: "2020", alternativeUrl: "/wunderlist/", icon: <ListTodo className="w-4 h-4" />, color: "bg-red-500/10 text-red-500" },
  { name: "AltspaceVR", tagline: "Social VR", shutdownYear: "2023", alternativeUrl: "/altspacevr/", icon: <Glasses className="w-4 h-4" />, color: "bg-cyan-500/10 text-cyan-500" },
  { name: "Google Wave", tagline: "Collaboration", shutdownYear: "2012", alternativeUrl: "/google-wave/", icon: <Waves className="w-4 h-4" />, color: "bg-yellow-500/10 text-yellow-500" },
  { name: "HipChat", tagline: "Team Chat", shutdownYear: "2019", alternativeUrl: "/hipchat/", icon: <MessageSquare className="w-4 h-4" />, color: "bg-indigo-500/10 text-indigo-500" },
  { name: "Stride", tagline: "Team Messaging", shutdownYear: "2019", alternativeUrl: "/stride/", icon: <Waves className="w-4 h-4" />, color: "bg-violet-500/10 text-violet-500" },
  { name: "Sunrise Calendar", tagline: "Calendar App", shutdownYear: "2016", alternativeUrl: "/sunrise-calendar/", icon: <Calendar className="w-4 h-4" />, color: "bg-amber-500/10 text-amber-500" },
  { name: "Mailbox", tagline: "Email App", shutdownYear: "2016", alternativeUrl: "/mailbox/", icon: <MessageSquare className="w-4 h-4" />, color: "bg-sky-500/10 text-sky-500" },
  { name: "Yahoo Groups", tagline: "Online Community", shutdownYear: "2020", alternativeUrl: "/yahoo-groups/", icon: <Users className="w-4 h-4" />, color: "bg-purple-600/10 text-purple-600" },
  { name: "Google Spaces", tagline: "Group Sharing", shutdownYear: "2017", alternativeUrl: "/google-spaces/", icon: <MessageSquare className="w-4 h-4" />, color: "bg-blue-600/10 text-blue-600" },
  { name: "Quill", tagline: "Team Chat", shutdownYear: "2015", alternativeUrl: "/quill/", icon: <MessageSquare className="w-4 h-4" />, color: "bg-teal-500/10 text-teal-500" },
  { name: "Google Hangouts", tagline: "Video Chat", shutdownYear: "2022", alternativeUrl: "/google-hangouts/", icon: <Users className="w-4 h-4" />, color: "bg-green-600/10 text-green-600" },
  { name: "Chalkup", tagline: "Classroom Collaboration", shutdownYear: "2018", alternativeUrl: "/chalkup/", icon: <GraduationCap className="w-4 h-4" />, color: "bg-emerald-500/10 text-emerald-500" },
  { name: "TodaysMeet", tagline: "Backchannel Chat", shutdownYear: "2018", alternativeUrl: "/todaysmeet/", icon: <MessageSquare className="w-4 h-4" />, color: "bg-pink-500/10 text-pink-500" },
  { name: "Wikispaces", tagline: "Education Wiki", shutdownYear: "2018", alternativeUrl: "/wikispaces/", icon: <BookOpen className="w-4 h-4" />, color: "bg-lime-500/10 text-lime-500" },
  { name: "Astrid", tagline: "To-Do App", shutdownYear: "2013", alternativeUrl: "/astrid/", icon: <ListTodo className="w-4 h-4" />, color: "bg-rose-500/10 text-rose-500" },
  { name: "Producteev", tagline: "Team Tasks", shutdownYear: "2018", alternativeUrl: "/producteev/", icon: <ListTodo className="w-4 h-4" />, color: "bg-fuchsia-500/10 text-fuchsia-500" },
  { name: "Sandglaz", tagline: "Kanban Tasks", shutdownYear: "2016", alternativeUrl: "/sandglaz/", icon: <ListTodo className="w-4 h-4" />, color: "bg-amber-600/10 text-amber-600" },
  { name: "Fetchnotes", tagline: "Smart Notes", shutdownYear: "2017", alternativeUrl: "/fetchnotes/", icon: <MessageSquare className="w-4 h-4" />, color: "bg-cyan-600/10 text-cyan-600" },
  { name: "IQTell", tagline: "GTD Productivity", shutdownYear: "2016", alternativeUrl: "/iqtell/", icon: <ListTodo className="w-4 h-4" />, color: "bg-violet-600/10 text-violet-600" },
  { name: "Height", tagline: "AI Project Manager", shutdownYear: "2024", alternativeUrl: "/height/", icon: <ListTodo className="w-4 h-4" />, color: "bg-blue-700/10 text-blue-700" },
  { name: "Google Tables", tagline: "Database Platform", shutdownYear: "2021", alternativeUrl: "/google-tables/", icon: <ListTodo className="w-4 h-4" />, color: "bg-red-600/10 text-red-600" },
];

interface RelatedAlternativesProps {
  currentPlatform: string;
  maxItems?: number;
}

export const RelatedAlternatives = ({ currentPlatform, maxItems = 6 }: RelatedAlternativesProps) => {
  // Filter out current platform and get random selection
  const otherPlatforms = allPlatforms.filter(p => p.name.toLowerCase() !== currentPlatform.toLowerCase());
  
  // Shuffle and take maxItems
  const shuffled = [...otherPlatforms].sort(() => 0.5 - Math.random());
  const selectedPlatforms = shuffled.slice(0, maxItems);

  return (
    <section className="container mx-auto px-4 mb-16">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-2xl font-bold mb-6">Related Discontinued Platform Alternatives</h2>
        <p className="text-muted-foreground mb-6">
          Explore other discontinued productivity and study platforms that CSW has replaced:
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {selectedPlatforms.map((platform) => (
            <Link 
              key={platform.name}
              to={platform.alternativeUrl}
              className="flex items-center gap-3 p-4 bg-card rounded-lg border border-border/50 hover:border-primary/50 transition-colors"
            >
              <div className={`p-2 rounded-lg ${platform.color}`}>
                {platform.icon}
              </div>
              <div className="min-w-0">
                <p className="font-semibold truncate">{platform.name}</p>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span>{platform.tagline}</span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {platform.shutdownYear}
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
        <div className="mt-6 text-center">
          <Link to="/alternatives/" className="text-primary hover:underline font-medium">
            View All 25 Discontinued Platforms →
          </Link>
        </div>
      </div>
    </section>
  );
};
